import { Box, Dialog, DialogContent, Typography, IconButton, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DosenInput from '../../../../../pages/new-designs/components/DosenInput';
import DosenButton from '../../../../ui/Buttons/DosenButton';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (title: string, body: string) => Promise<void>;
}

export default function AddTextModal({ open, onClose, onCreate }: Props) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAdd = async () => {
    if (!title.trim() || !body.trim()) {
      setError('Both title and text are required.');
      return;
    }

    setLoading(true);
    try {
      await onCreate(title.trim(), body.trim());
      setTitle('');
      setBody('');
      setError(null);
      onClose();
    } catch (err) {
      setError('Failed to save text. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0F0F0F',
          borderRadius: 2,
          border: '1px solid #444444',
          overflow: 'hidden',
          maxWidth: 550
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative'
        }}
      >
        <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>
          Add text to your Program
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#FDFDFD' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          bgcolor: '#0F0F0F',
          px: 3,
          py: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5
        }}
      >
        <DosenInput
          label="Title"
          fullWidth
          value={title}
          onChange={(e: any) => setTitle(e.target.value)}
        />
        <DosenInput
          label="Insert text"
          fullWidth
          multiline
          minRows={4}
          value={body}
          onChange={(e: any) => setBody(e.target.value)}
        />
        {error && <Typography sx={{ color: 'error.main', mt: 1 }}>{error}</Typography>}
        <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 400, lineHeight: '18px' }}>
          Add text (by either typing now or by adding from sources like Notion) that we can use to create your program
        </Typography>

        <Box display="flex" justifyContent="center">
          <DosenButton
            onClick={handleAdd}
            disabled={!title.trim() || !body.trim() || loading}
            sx={{ minWidth: 200, position: 'relative' }}
          >
            {loading && <CircularProgress size={18} sx={{ color: '#FDFDFD', position: 'absolute', left: 12 }} />}
            Add Text
          </DosenButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
